import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { Upload, Delete, Edit, GetApp, Link as LinkIcon, Visibility } from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import { ROLES } from '../../constants/roles';
import {
  downloadCertificateVersion,
  downloadUploadedFile,
  previewCertificateVersion,
  previewUploadedFile
} from '../../utils/certificateTrainingFiles';

const CERTIFICATE_TYPES = [
  { value: 'DSC', label: 'Digital Signature Certificate' },
  { value: 'PILOT_LICENSE', label: 'Pilot License' },
  { value: 'PROFESSIONAL_LICENSE', label: 'Professional License' },
  { value: 'QUALIFICATION_CERTIFICATE', label: 'Qualification Certificate' },
  { value: 'AIRCRAFT_ENDORSEMENT', label: 'Aircraft Endorsement' },
  { value: 'OTHER', label: 'Other' }
];

const formatDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : 'N/A');

const getEndpointBase = (mode, entityId) => {
  if (mode === 'user') {
    return `/api/users/${entityId}/certificates`;
  }
  return `/api/users/onboard/${entityId}/certificates`;
};

const CertificateManagement = ({ entityId, mode = 'onboarding', readOnly = false, userRole = null, hideTitle = false }) => {
  const canFullyEditCertificate = [
    ROLES.SUPER_ADMIN,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.OPERATIONS_MANAGER,
    ROLES.OPERATIONS_EXECUTIVE,
    ROLES.HR_MANAGER
  ].includes(userRole);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    type: CERTIFICATE_TYPES[0].value,
    name: '',
    acquiredDate: dayjs().format('YYYY-MM-DD'),
    performedOnDate: '',
    expiryDate: '',
    periodicityMonths: '',
    hasNoExpiry: false,
    certificateUrl: '',
    remarks: '',
    file: null
  });

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getEndpointBase(mode, entityId));
      const list = response.data || [];
      // Closest expiry first (same as Crew Duty Trainings tab); no-expiry last
      const today = dayjs().startOf('day');
      list.sort((a, b) => {
        const daysA = a.hasNoExpiry || !a.expiryDate
          ? Number.POSITIVE_INFINITY
          : dayjs(a.expiryDate).startOf('day').diff(today, 'day');
        const daysB = b.hasNoExpiry || !b.expiryDate
          ? Number.POSITIVE_INFINITY
          : dayjs(b.expiryDate).startOf('day').diff(today, 'day');
        if (daysA !== daysB) return daysA - daysB;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
      setItems(list);
    } catch (err) {
      console.error('Failed to load certificates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, mode]);

  const resetForm = () => {
    setFormData({
      type: CERTIFICATE_TYPES[0].value,
      name: '',
      acquiredDate: dayjs().format('YYYY-MM-DD'),
      performedOnDate: '',
      expiryDate: '',
      periodicityMonths: '',
      hasNoExpiry: false,
      certificateUrl: '',
      remarks: '',
      file: null
    });
    setEditItem(null);
    setError('');
  };

  const openDialog = (certificate = null) => {
    if (certificate) {
      setEditItem(certificate);
      setFormData({
        type: certificate.type,
        name: certificate.name,
        acquiredDate: formatDate(certificate.acquiredDate),
        performedOnDate: certificate.performedOnDate ? formatDate(certificate.performedOnDate) : '',
        expiryDate: certificate.expiryDate ? formatDate(certificate.expiryDate) : '',
        periodicityMonths: certificate.periodicityMonths != null ? String(certificate.periodicityMonths) : '',
        hasNoExpiry: certificate.hasNoExpiry,
        certificateUrl: certificate.certificateUrl || '',
        remarks: certificate.remarks || '',
        file: null
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleFormChange = (field) => (event) => {
    const value = field === 'hasNoExpiry' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event) => {
    setFormData((prev) => ({ ...prev, file: event.target.files?.[0] || null }));
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (!editItem) {
        if (!formData.file && !formData.certificateUrl) {
          setError('Either a file upload or certificate URL is required.');
          return;
        }
        const hasExpiry = !formData.hasNoExpiry;
        if (hasExpiry) {
          const months = parseInt(formData.periodicityMonths, 10);
          if (!Number.isFinite(months) || months <= 0) {
            setError('Periodicity (in months) is required and must be a whole number greater than 0 when certificate expires.');
            return;
          }
          if (!formData.expiryDate) {
            setError('Expiry Date could not be calculated. Please check acquired date and periodicity.');
            return;
          }
        }
        const data = new FormData();
        if (formData.file) {
          data.append('file', formData.file);
        }
        data.append('type', formData.type);
        data.append('name', formData.name);
        data.append('acquiredDate', formData.acquiredDate);
        if (formData.performedOnDate) {
          data.append('performedOnDate', formData.performedOnDate);
        }
        if (!formData.hasNoExpiry && formData.expiryDate) {
          data.append('expiryDate', formData.expiryDate);
        }
        if (!formData.hasNoExpiry && formData.periodicityMonths) {
          data.append('periodicityMonths', String(parseInt(formData.periodicityMonths, 10)));
        }
        data.append('hasNoExpiry', formData.hasNoExpiry);
        if (formData.certificateUrl) {
          data.append('certificateUrl', formData.certificateUrl);
        }
        if (formData.remarks) data.append('remarks', formData.remarks);
        await axios.post(getEndpointBase(mode, entityId), data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const canFullyEdit = canFullyEditCertificate;
        const originalAcquired = editItem.acquiredDate
          ? dayjs(editItem.acquiredDate).format('YYYY-MM-DD')
          : '';
        const acquiredChanged = Boolean(
          canFullyEdit && formData.acquiredDate && formData.acquiredDate !== originalAcquired
        );
        const isExpired = Boolean(
          editItem.expiryDate && !editItem.hasNoExpiry && dayjs(editItem.expiryDate).isBefore(dayjs(), 'day')
        );
        const isRenewal = acquiredChanged || isExpired || Boolean(formData.file);

        if (isRenewal && !formData.file) {
          setError('A new certificate file must be uploaded when renewing. Previous files stay available.');
          return;
        }

        // For updates, allow file replacement — previous files are archived, not deleted
        if (formData.file) {
          const data = new FormData();
          data.append('file', formData.file);
          if (isRenewal) data.append('isRenewal', 'true');
          // SUPER_ADMIN / OPERATIONS_MANAGER can modify all fields
          if (canFullyEdit) {
            data.append('type', formData.type);
            data.append('name', formData.name);
            data.append('acquiredDate', formData.acquiredDate);
          }
          // Always send performedOnDate (even if empty) so it can be cleared
          data.append('performedOnDate', formData.performedOnDate || '');
          data.append('expiryDate', formData.hasNoExpiry ? '' : (formData.expiryDate || ''));
          if (!formData.hasNoExpiry && formData.periodicityMonths) {
            data.append('periodicityMonths', String(parseInt(formData.periodicityMonths, 10)));
          }
          data.append('hasNoExpiry', formData.hasNoExpiry);
          if (formData.certificateUrl) {
            data.append('certificateUrl', formData.certificateUrl);
          }
          if (formData.remarks) {
            data.append('remarks', formData.remarks);
          }
          await axios.put(`${getEndpointBase(mode, entityId)}/${editItem._id}`, data, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          const updateData = {
            expiryDate: formData.hasNoExpiry ? null : formData.expiryDate,
            periodicityMonths: formData.hasNoExpiry || !formData.periodicityMonths
              ? undefined
              : parseInt(formData.periodicityMonths, 10),
            hasNoExpiry: formData.hasNoExpiry,
            certificateUrl: formData.certificateUrl,
            remarks: formData.remarks
          };
          if (formData.performedOnDate) {
            updateData.performedOnDate = formData.performedOnDate;
          } else {
            updateData.performedOnDate = null;
          }
          // SUPER_ADMIN / OPERATIONS_MANAGER can modify all fields
          if (canFullyEdit) {
            updateData.type = formData.type;
            updateData.name = formData.name;
            updateData.acquiredDate = formData.acquiredDate;
          }
          await axios.put(`${getEndpointBase(mode, entityId)}/${editItem._id}`, updateData);
        }
      }
      closeDialog();
      fetchItems();
    } catch (err) {
      console.error('Certificate save error:', err);
      setError(err.response?.data?.message || 'Failed to save certificate.');
    }
  };

  // Auto-calculate expiry date when periodicity months and acquired date are set, and certificate has expiry
  React.useEffect(() => {
    if (formData.hasNoExpiry) {
      return;
    }
    const months = parseInt(formData.periodicityMonths, 10);
    if (!Number.isFinite(months) || months <= 0) {
      return;
    }
    if (!formData.acquiredDate) {
      return;
    }
    const nextExpiry = dayjs(formData.acquiredDate).add(months, 'month').format('YYYY-MM-DD');
    setFormData((prev) => {
      if (prev.expiryDate === nextExpiry) {
        return prev;
      }
      return { ...prev, expiryDate: nextExpiry };
    });
  }, [formData.acquiredDate, formData.periodicityMonths, formData.hasNoExpiry]);

  const handleDelete = async (certificate) => {
    if (!window.confirm('Delete this certificate?')) return;
    try {
      await axios.delete(`${getEndpointBase(mode, entityId)}/${certificate._id}`);
      fetchItems();
    } catch (err) {
      console.error('Certificate delete error:', err);
    }
  };

  return (
    <Box mt={hideTitle ? 0 : 4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        {!hideTitle && <Typography variant="h6">Certificates</Typography>}
        {hideTitle && <Box />}
        {!readOnly && (
          <Button variant="outlined" startIcon={<Upload />} onClick={() => openDialog()}>
            Upload Certificate
          </Button>
        )}
      </Stack>

      {loading ? (
        <Typography>Loading certificates...</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Acquired</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Remarks</TableCell>
              {!readOnly && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((certificate) => {
              const isExpired = certificate.expiryDate && dayjs(certificate.expiryDate).isBefore(dayjs(), 'day');
              const isExpiringSoon = certificate.expiryDate && dayjs(certificate.expiryDate).diff(dayjs(), 'day') <= 60 && !isExpired;
              return (
                <TableRow key={certificate._id} hover>
                  <TableCell>{certificate.name}</TableCell>
                  <TableCell>{certificate.type?.replace(/_/g, ' ')}</TableCell>
                  <TableCell>{formatDate(certificate.acquiredDate)}</TableCell>
                  <TableCell>
                    {certificate.hasNoExpiry ? (
                      <Chip label="No Expiry" size="small" color="default" />
                    ) : (
                      <Chip
                        label={formatDate(certificate.expiryDate)}
                        size="small"
                        color={isExpired ? 'error' : (isExpiringSoon ? 'warning' : 'default')}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {certificate.fileUrl ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={async () => {
                            try {
                              await previewUploadedFile({
                                mode,
                                entityId,
                                resource: 'certificates',
                                itemId: certificate._id,
                                mimeType: certificate.mimeType
                              });
                            } catch (error) {
                              console.error('Preview error:', error);
                              alert(error.message || 'Failed to preview file. Please try again.');
                            }
                          }}
                          variant="outlined"
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          startIcon={<GetApp />}
                          onClick={async () => {
                            try {
                              await downloadUploadedFile({
                                mode,
                                entityId,
                                resource: 'certificates',
                                itemId: certificate._id,
                                fileName: certificate.fileName || 'certificate',
                                mimeType: certificate.mimeType
                              });
                            } catch (error) {
                              console.error('Download error:', error);
                              alert('Failed to download file. Please try again.');
                            }
                          }}
                          variant="outlined"
                        >
                          Download
                        </Button>
                      </Stack>
                    ) : certificate.certificateUrl ? (
                      <Button
                        size="small"
                        startIcon={<LinkIcon />}
                        href={certificate.certificateUrl}
                        target="_blank"
                        variant="outlined"
                      >
                        View URL
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>{certificate.remarks || '—'}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openDialog(certificate)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(certificate)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={readOnly ? 6 : 7} align="center">No certificates available.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Update Certificate' : 'Upload Certificate'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Certificate Type"
              value={formData.type}
              onChange={handleFormChange('type')}
              fullWidth
              disabled={!!editItem && !canFullyEditCertificate}
            >
              {CERTIFICATE_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleFormChange('name')}
              fullWidth
              disabled={!!editItem && !canFullyEditCertificate}
            />
            <TextField
              label="Acquired Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.acquiredDate}
              onChange={handleFormChange('acquiredDate')}
              fullWidth
              disabled={!!editItem && !canFullyEditCertificate}
              helperText="Date when the certificate was acquired or issued"
            />
            <TextField
              label="Performed On Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.performedOnDate}
              onChange={handleFormChange('performedOnDate')}
              fullWidth
              helperText="Date when the certificate exam/training was actually performed (can be before or after acquired date)"
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Expiry Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.hasNoExpiry ? '' : formData.expiryDate}
                onChange={canFullyEditCertificate ? handleFormChange('expiryDate') : undefined}
                fullWidth
                disabled={!canFullyEditCertificate}
                helperText={formData.hasNoExpiry ? '' : (canFullyEditCertificate ? 'Manually set expiry date' : 'Auto-calculated from acquired date and periodicity')}
              />
              <TextField
                select
                label="Has Expiry?"
                value={formData.hasNoExpiry ? 'no' : 'yes'}
                onChange={(event) => {
                  const value = event.target.value === 'yes';
                  setFormData((prev) => ({
                    ...prev,
                    hasNoExpiry: !value,
                    // Clear periodicity and expiry when switching to No Expiry
                    periodicityMonths: value ? prev.periodicityMonths : '',
                    expiryDate: value ? prev.expiryDate : ''
                  }));
                }}
                sx={{ width: 150 }}
              >
                <MenuItem value="yes">Expires</MenuItem>
                <MenuItem value="no">No Expiry</MenuItem>
              </TextField>
            </Stack>
            {!formData.hasNoExpiry && (
              <TextField
                label="Periodicity (in months)"
                type="number"
                value={formData.periodicityMonths}
                onChange={handleFormChange('periodicityMonths')}
                fullWidth
                required
                inputProps={{ min: 1, step: 1 }}
                helperText="Enter a whole number of months; expiry date will be auto-calculated."
              />
            )}
            {editItem && (editItem.fileUrl || editItem.certificateUrl || (editItem.previousVersions || []).length > 0) && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Certificate history</Typography>
                {(editItem.fileUrl || editItem.certificateUrl) && (
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 70 }}>Current</Typography>
                    {editItem.fileUrl && (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={async () => {
                            try {
                              await previewUploadedFile({
                                mode,
                                entityId,
                                resource: 'certificates',
                                itemId: editItem._id,
                                mimeType: editItem.mimeType
                              });
                            } catch (error) {
                              console.error('Preview error:', error);
                              alert(error.message || 'Failed to preview file. Please try again.');
                            }
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<GetApp />}
                          onClick={async () => {
                            try {
                              await downloadUploadedFile({
                                mode,
                                entityId,
                                resource: 'certificates',
                                itemId: editItem._id,
                                fileName: editItem.fileName || 'certificate',
                                mimeType: editItem.mimeType
                              });
                            } catch (error) {
                              console.error('Download error:', error);
                              alert('Failed to download file. Please try again.');
                            }
                          }}
                        >
                          Download ({editItem.fileName || 'file'})
                        </Button>
                      </>
                    )}
                    {editItem.certificateUrl && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LinkIcon />}
                        href={editItem.certificateUrl}
                        target="_blank"
                      >
                        Open URL
                      </Button>
                    )}
                  </Stack>
                )}
                {(editItem.previousVersions || []).slice().reverse().map((version, idx) => (
                  <Stack
                    key={version._id || idx}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 70 }}>Previous</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {version.fileName || 'Prior file'}
                      {version.acquiredDate ? ` · ${dayjs(version.acquiredDate).format('DD MMM YYYY')}` : ''}
                      {version.archivedAt ? ` · archived ${dayjs(version.archivedAt).format('DD MMM YYYY')}` : ''}
                    </Typography>
                    {(version.fileName || version.fileUrl) ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={async () => {
                            try {
                              await previewCertificateVersion({
                                mode,
                                entityId,
                                certId: editItem._id,
                                versionId: version._id,
                                mimeType: version.mimeType
                              });
                            } catch (error) {
                              alert(error.message || 'Failed to preview previous certificate.');
                            }
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<GetApp />}
                          onClick={async () => {
                            try {
                              await downloadCertificateVersion({
                                mode,
                                entityId,
                                certId: editItem._id,
                                versionId: version._id,
                                fileName: version.fileName || 'previous-certificate',
                                mimeType: version.mimeType
                              });
                            } catch (error) {
                              alert(error.message || 'Failed to download previous certificate.');
                            }
                          }}
                        >
                          Download
                        </Button>
                      </>
                    ) : version.certificateUrl ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LinkIcon />}
                        href={version.certificateUrl}
                        target="_blank"
                      >
                        Open URL
                      </Button>
                    ) : null}
                  </Stack>
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Renewing archives the current file — previous certificates remain available.
                </Typography>
              </Box>
            )}
            <Button variant="outlined" component="label">
              {formData.file
                ? formData.file.name
                : (editItem ? 'Upload renewed / replacement file' : 'Select File')}
              <input type="file" hidden onChange={handleFileChange} accept=".pdf,image/*" />
            </Button>
            <TextField
              label="Certificate URL (Optional)"
              value={formData.certificateUrl}
              onChange={handleFormChange('certificateUrl')}
              fullWidth
              helperText={editItem ? "Update the certificate URL if needed" : "Provide a URL to the certificate if not uploading a file"}
            />
            <TextField
              label="Remarks"
              multiline
              minRows={2}
              value={formData.remarks}
              onChange={handleFormChange('remarks')}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editItem ? 'Save' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CertificateManagement;
